// mobile/screens/PesticideList.js
import React, { useEffect, useState, useCallback, useMemo } from 'react';
import {
  View,
  FlatList,
  Text,
  Alert,
  Modal,
  TextInput,
  TouchableOpacity,
  ActivityIndicator,
  Platform,
  StyleSheet,
  RefreshControl,
  ScrollView,
  KeyboardAvoidingView,
} from 'react-native';
import axios from 'axios';
import { API_BASE } from '../utils';
import * as Print from 'expo-print';
import * as Sharing from 'expo-sharing';
import { Ionicons } from '@expo/vector-icons';
import { SafeAreaView } from 'react-native-safe-area-context';
import { useFocusEffect } from '@react-navigation/native';
import EnterpriseHeader from '../components/EnterpriseHeader';
import { PADIPRO, padiproShadow, padiproShadowMd } from '../components/padiproTheme';
import { useResponsive } from '../components/responsive';

// --- Presets ---
const COMPANY_PRESETS = ['Pupuk Indonesia', 'Petrokimia', 'Bayer', 'FMC', 'Syngenta', 'Corteva', 'BASF', 'Dll'];
const CATEGORY_PRESETS = ['Pupuk', 'Insektisida', 'Fungisida', 'Herbisida', 'Moluskisida', 'Bakterisida'];

const log = (...args) => console.log('[PRINT]', ...args);

const absolutizeHtml = (html) => {
  const base = API_BASE.replace(/\/+$/, '');
  let fixed = html;
  let rewrites = { src: 0, href: 0 };

  const hasHead = /<head[^>]*>/i.test(fixed);
  const hasBase = /<base\s+href=/i.test(fixed);

  if (!hasHead) {
    fixed = `<!doctype html><html><head></head><body>${fixed}</body></html>`;
    log('No <head> found → wrapped with full HTML skeleton');
  }
  if (!hasBase) {
    fixed = fixed.replace(/<head[^>]*>/i, (m) => `${m}\n<base href="${base}/">`);
  }

  fixed = fixed.replace(/\ssrc="\/(?!\/)/gi, () => {
    rewrites.src++;
    return ` src="${base}/`;
  });
  fixed = fixed.replace(/\shref="\/(?!\/)/gi, () => {
    rewrites.href++;
    return ` href="${base}/`;
  });

  log('Rewrite counts:', rewrites);
  return fixed;
};

const injectAutoPrintScript = (html) => {
  const script = `
    <script>
      (function(){
        function whenAllImagesReady(cb){
          var imgs = Array.prototype.slice.call(document.images || []);
          if (imgs.length === 0) { cb(); return; }
          var doneCount = 0;
          function done(){ doneCount++; if (doneCount >= imgs.length) cb(); }
          imgs.forEach(function(img){
            if (img.complete) { done(); }
            else {
              img.addEventListener('load', done, { once:true });
              img.addEventListener('error', done, { once:true });
            }
          });
        }
        whenAllImagesReady(function(){
          try { window.focus(); } catch(e){}
          setTimeout(function(){
            try { window.print(); } catch(e){}
            setTimeout(function(){ try { window.close(); } catch(e){} }, 1500);
          }, 150);
        });
      })();
    </script>
  `;

  if (/<\/body>/i.test(html)) return html.replace(/<\/body>/i, `${script}\n</body>`);
  return `${html}${script}`;
};

const formatIDR = (n) => `Rp ${Number(n || 0).toLocaleString('id-ID')}`;
const normText = (v) => String(v ?? '').trim().toLowerCase();
const isFertilizerCategory = (category) => ['pupuk', 'fertilizer'].includes(normText(category));
const inferFertilizerSubcategory = (product) => {
  const hay = `${product?.name || ''} ${product?.category || ''}`
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();

  if (!hay) return 'pupuk_lainnya';
  if (/(leili|leilei|semprot|daun|foliar|ultradap|highscore|kenfolan|zinatra|hiperzink|zinc|ambition|provit|zimab)/.test(hay)) return 'pupuk_semprot';
  if (/(phoska|ponska)\s*plus/.test(hay)) return 'phoska_plus';
  if (/(phoska|ponska)/.test(hay)) return 'phoska';
  if (/fertiphos/.test(hay)) return 'fertiphos';
  if (/(tsp|tawon|ksp)/.test(hay)) return 'tsp_tawon';
  if (/(^| )urea( |$)/.test(hay)) return 'urea';
  if (/(^| )za( |$)/.test(hay) || /pupuk\s+za/.test(hay)) return 'za';
  if (/(dolomit|dolomite|kapur)/.test(hay)) return 'dolomit';
  return 'pupuk_lainnya';
};
const inventoryKindLabel = (product) => (isFertilizerCategory(product?.category) ? 'Pupuk' : 'Pestisida');
const inventoryExpenseCategory = (product) => (isFertilizerCategory(product?.category) ? 'pupuk' : 'pestisida');
const inventoryExpenseSubcategory = (product) => (
  isFertilizerCategory(product?.category) ? inferFertilizerSubcategory(product) : (product?.category || null)
);

const normalizeFields = (rows) => {
  const list = Array.isArray(rows) ? rows : [];
  return list
    .map((x) => ({
      id: Number(x.id ?? x.field_id ?? x.fieldId ?? 0),
      name: String(x.name ?? x.field_name ?? ''),
      area_rante:
        x.area_rante != null
          ? Number(x.area_rante)
          : x.rante != null
            ? Number(x.rante)
            : null,
    }))
    .filter((x) => x.id > 0 && x.name);
};

export default function PesticideList({ route }) {
  const { token, query: parentQuery, embedded = false, seasonId = null, refreshKey = 0 } = route.params;

  const headers = useMemo(
    () => ({
      Authorization: `Bearer ${token}`,
    }),
    [token]
  );

  const { contentPadding, contentWidthStyle, isPhoneLike, isTiny } = useResponsive();
  const isSmallWeb = Platform.OS === 'web' && (isPhoneLike || isTiny);
  const isHandset = isPhoneLike || isTiny;

  const STOCK_BADGE_MIN_W = isSmallWeb ? 58 : 88;
  const STOCK_BADGE_PV = isSmallWeb ? 7 : 10;
  const STOCK_NUM_SIZE = isSmallWeb ? 14 : 22;
  const STOCK_UNIT_SIZE = isSmallWeb ? 10 : 12;

  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(false);
  const [refreshing, setRefreshing] = useState(false);

  const externalQuery = typeof parentQuery === 'string' ? parentQuery : null;
  const [query, setQuery] = useState(externalQuery ?? '');
  const [filterQuery, setFilterQuery] = useState(externalQuery ?? '');
  useEffect(() => {
    if (externalQuery !== null) {
      setQuery(externalQuery);
      setFilterQuery(externalQuery);
    }
  }, [externalQuery]);
  useEffect(() => {
    if (externalQuery !== null) return undefined;
    const t = setTimeout(() => setFilterQuery(query), 180);
    return () => clearTimeout(t);
  }, [query, externalQuery]);

  const [selectedCategory, setSelectedCategory] = useState('All');
  const [selectedCompany, setSelectedCompany] = useState('All');

  // Restock modal
  const [restockOpen, setRestockOpen] = useState(false);
  const [selectedId, setSelectedId] = useState(null);
  const [qty, setQty] = useState('1');
  const [restockUnitPrice, setRestockUnitPrice] = useState('');
  const [note, setNote] = useState('');
  const [priceOpen, setPriceOpen] = useState(false);
  const [priceId, setPriceId] = useState(null);
  const [priceInput, setPriceInput] = useState('');

  // Use modal
  const [useOpen, setUseOpen] = useState(false);
  const [useId, setUseId] = useState(null);
  const [useQty, setUseQty] = useState('1');
  const [useDate, setUseDate] = useState('');
  const [useNote, setUseNote] = useState('');

  // Assignment UI
  const [assignMode, setAssignMode] = useState('general'); // general | fields
  const [splitMethod, setSplitMethod] = useState('area'); // equal | area
  const [fieldsList, setFieldsList] = useState([]);
  const [fieldsLoading, setFieldsLoading] = useState(false);
  const [selectedFields, setSelectedFields] = useState([]);

  // Print
  const [printModalOpen, setPrintModalOpen] = useState(false);
  const [printTarget, setPrintTarget] = useState(null);
  const [printSize, setPrintSize] = useState(50);
  const PRINT_SIZES = [50, 40, 30];

  const fetchList = useCallback(async () => {
    try {
      const r = await axios.get(`${API_BASE}/pesticides`, {
        params: { season_id: seasonId || 'active' },
        headers,
      });
      setList(r.data || []);
    } catch (e) {
      Alert.alert('Error', e?.response?.data?.error || e?.message || 'Gagal memuat data');
    }
  }, [headers, seasonId]);

  const fetchFieldsCompat = useCallback(async () => {
    setFieldsLoading(true);
    try {
      const r1 = await axios.get(`${API_BASE}/operations/fields?season=active`, { headers });
      const rows1 = r1?.data?.fields || r1?.data?.items || r1?.data || [];
      const n1 = normalizeFields(rows1);
      if (n1.length) {
        setFieldsList(n1);
        return;
      }

      const r2 = await axios.get(`${API_BASE}/fields`, { headers });
      const rows2 = r2?.data?.fields || r2?.data?.items || r2?.data || [];
      setFieldsList(normalizeFields(rows2));
    } catch (e) {
      console.warn('[fields] fetch fail:', e?.response?.data || e?.message);
      setFieldsList([]);
    } finally {
      setFieldsLoading(false);
    }
  }, [headers]);

  useEffect(() => {
    fetchList();
  }, [fetchList, refreshKey]);

  useFocusEffect(
    useCallback(() => {
      fetchList();
    }, [fetchList, refreshKey])
  );

  const onRefresh = async () => {
    setRefreshing(true);
    await fetchList();
    setRefreshing(false);
  };

  const categories = useMemo(() => {
    const s = new Set(CATEGORY_PRESETS);
    (list || []).forEach((it) => {
      if (it.category) s.add(it.category);
    });
    return ['All', ...Array.from(s).sort((a, b) => a.localeCompare(b))];
  }, [list]);

  const companies = useMemo(() => {
    const s = new Set(COMPANY_PRESETS);
    (list || []).forEach((it) => {
      if (it.company) s.add(it.company);
    });
    return ['All', ...Array.from(s).sort((a, b) => a.localeCompare(b))];
  }, [list]);

  const norm = normText;

  const filtered = useMemo(() => {
    const q = norm(filterQuery);
    const selCat = selectedCategory === 'All' ? null : norm(selectedCategory);
    const selCom = selectedCompany === 'All' ? null : norm(selectedCompany);

    const out = (list || []).filter((it) => {
      const cat = norm(it.category);
      const com = norm(it.company);

      if (selCat && cat !== selCat) return false;
      if (selCom && com !== selCom) return false;

      if (!q) return true;
      const hay = `${it.name || ''} ${it.category || ''} ${it.company || ''}`.toLowerCase();
      return hay.includes(q);
    });

    out.sort((a, b) => {
      const sa = Number(a.stock) || 0;
      const sb = Number(b.stock) || 0;
      if (sb !== sa) return sb - sa;
      return String(a.name || '').localeCompare(String(b.name || ''), 'id', { sensitivity: 'base' });
    });

    return out;
  }, [list, filterQuery, selectedCategory, selectedCompany]);

  const inventorySummary = useMemo(() => {
    const rows = filtered || [];
    let totalValue = 0;
    let totalStock = 0;
    let pricedItems = 0;
    let missingPrice = 0;
    let lowStock = 0;
    let topItem = null;

    for (const it of rows) {
      const stock = Number(it.stock || 0);
      const price = Number(it.unit_price || 0);
      const value = stock > 0 && price > 0 ? stock * price : 0;
      totalStock += stock;
      totalValue += value;
      if (price > 0) pricedItems += 1;
      else if (stock > 0) missingPrice += 1;
      if (stock <= 3) lowStock += 1;
      if (!topItem || value > topItem.value) {
        topItem = { name: it.name || '-', value };
      }
    }

    return {
      itemCount: rows.length,
      totalStock,
      totalValue,
      pricedItems,
      missingPrice,
      lowStock,
      topItem: topItem && topItem.value > 0 ? topItem : null,
    };
  }, [filtered]);

  const resetFilters = () => {
    if (externalQuery === null) {
      setQuery('');
      setFilterQuery('');
    }
    setSelectedCategory('All');
    setSelectedCompany('All');
  };

  const selectedPesticide = useMemo(
    () => (list || []).find((x) => Number(x.pesticide_id) === Number(useId)) || null,
    [list, useId]
  );
  const selectedRestockPesticide = useMemo(
    () => (list || []).find((x) => Number(x.pesticide_id) === Number(selectedId)) || null,
    [list, selectedId]
  );
  const selectedPricePesticide = useMemo(
    () => (list || []).find((x) => Number(x.pesticide_id) === Number(priceId)) || null,
    [list, priceId]
  );

  const toggleFieldPick = (id) => {
    const fid = Number(id);
    if (!fid) return;
    setSelectedFields((prev) => (prev.includes(fid) ? prev.filter((x) => x !== fid) : [...prev, fid]));
  };

  const selectedFieldObjs = useMemo(() => {
    const map = new Map(fieldsList.map((f) => [f.id, f]));
    return selectedFields.map((id) => map.get(id)).filter(Boolean);
  }, [fieldsList, selectedFields]);
  const selectedAreaTotal = useMemo(
    () => selectedFieldObjs.reduce((sum, f) => sum + (Number(f.area_rante || 0) || 0), 0),
    [selectedFieldObjs]
  );
  const allFieldsSelected = fieldsList.length > 0 && selectedFields.length === fieldsList.length;
  const pickAllFields = () => setSelectedFields((fieldsList || []).map((f) => f.id).filter(Boolean));
  const resetFieldPick = () => setSelectedFields([]);

  const useTotalPreview = useMemo(() => {
    const q = Number(useQty);
    const unitPrice = Number(selectedPesticide?.unit_price || 0);
    if (!Number.isFinite(q) || q <= 0) return 0;
    if (!Number.isFinite(unitPrice) || unitPrice <= 0) return 0;
    return q * unitPrice;
  }, [useQty, selectedPesticide]);
  const useQtyNum = Number(useQty || 0);
  const useUnitPrice = Number(selectedPesticide?.unit_price || 0);
  const useStockNow = Number(selectedPesticide?.stock || 0);
  const useStockAfter = Number.isFinite(useQtyNum) ? Math.max(0, useStockNow - useQtyNum) : useStockNow;
  const useSubmitDisabled = loading || (assignMode === 'fields' && selectedFields.length === 0);

  const splitPreview = useMemo(() => {
    const totalVal = Number(useTotalPreview || 0);
    if (!(totalVal > 0)) return [];
    if (assignMode !== 'fields') return [];
    if (selectedFieldObjs.length === 0) return [];

    if (splitMethod === 'equal') {
      const each = totalVal / selectedFieldObjs.length;
      return selectedFieldObjs.map((f) => ({
        id: f.id,
        name: f.name,
        amount: each,
      }));
    }

    const validAreas = selectedFieldObjs.map((f) => Number(f.area_rante || 0));
    const sumArea = validAreas.reduce((a, b) => a + (Number.isFinite(b) ? b : 0), 0);

    if (!(sumArea > 0)) {
      const each = totalVal / selectedFieldObjs.length;
      return selectedFieldObjs.map((f) => ({
        id: f.id,
        name: f.name,
        amount: each,
      }));
    }

    return selectedFieldObjs.map((f) => {
      const area = Number(f.area_rante || 0);
      return {
        id: f.id,
        name: f.name,
        amount: totalVal * (area / sumArea),
      };
    });
  }, [useTotalPreview, assignMode, splitMethod, selectedFieldObjs]);

  const openRestock = (id) => {
    setSelectedId(id);
    const product = (list || []).find((x) => Number(x.pesticide_id) === Number(id));
    setQty('1');
    setRestockUnitPrice(product?.unit_price ? String(Number(product.unit_price)) : '');
    setNote('');
    setRestockOpen(true);
  };

  const doRestock = async () => {
    const q = parseInt(qty, 10);
    if (!Number.isFinite(q) || q <= 0) {
      Alert.alert('Qty salah', 'Masukkan jumlah > 0');
      return;
    }
    const price = Number(restockUnitPrice);
    const product = (list || []).find((x) => Number(x.pesticide_id) === Number(selectedId));
    const fallbackPrice = Number(product?.unit_price || 0);
    const priceToSend = Number.isFinite(price) && price > 0
      ? price
      : (fallbackPrice > 0 ? fallbackPrice : null);
    if (loading) return;

    setLoading(true);
    try {
      await axios.post(
        `${API_BASE}/pesticides/restock`,
        { pesticide_id: selectedId, qty: q, unit_price: priceToSend, note },
        { headers }
      );
      setRestockOpen(false);
      await fetchList();
      Alert.alert('Sukses', `Stok ditambah ${q}`);
    } catch (err) {
      Alert.alert('Error', err?.response?.data?.error || err?.message || 'Gagal restock');
    } finally {
      setLoading(false);
    }
  };

  const openPriceModal = (item) => {
    setPriceId(item?.pesticide_id || null);
    setPriceInput(item?.unit_price ? String(Number(item.unit_price)) : '');
    setPriceOpen(true);
  };

  const doUpdatePrice = async () => {
    const price = Number(priceInput);
    if (!priceId) return;
    if (!Number.isFinite(price) || price <= 0) {
      Alert.alert('Harga salah', 'Masukkan harga per unit > 0.');
      return;
    }
    if (loading) return;

    setLoading(true);
    try {
      await axios.patch(
        `${API_BASE}/pesticides/${priceId}/price`,
        { unit_price: price },
        { headers }
      );
      setPriceOpen(false);
      await fetchList();
      Alert.alert('Sukses', 'Harga per unit diperbarui.');
    } catch (err) {
      Alert.alert('Error', err?.response?.data?.error || err?.message || 'Gagal update harga');
    } finally {
      setLoading(false);
    }
  };

  const openUse = async (id) => {
    setUseId(id);
    setUseQty('1');
    setUseDate(new Date().toISOString().slice(0, 10));
    setUseNote('');
    setAssignMode('fields');
    setSplitMethod('area');
    setSelectedFields([]);
    setUseOpen(true);
    await fetchFieldsCompat();
  };

  const doUse = async () => {
    const q = parseInt(useQty, 10);
    if (!Number.isFinite(q) || q <= 0) {
      Alert.alert('Qty salah', 'Masukkan jumlah > 0');
      return;
    }
    if (loading) return;

    const current = Number((list || []).find((x) => x.pesticide_id === useId)?.stock) || 0;
    if (q > current) {
      Alert.alert('Qty terlalu besar', `Stok tersedia ${current}.`);
      return;
    }

    if (assignMode === 'fields' && selectedFields.length === 0) {
      Alert.alert('Validasi', 'Pilih minimal 1 lahan.');
      return;
    }
    const useDateFinal = String(useDate || '').trim();
    if (useDateFinal && !/^\d{4}-\d{2}-\d{2}$/.test(useDateFinal)) {
      Alert.alert('Tanggal salah', 'Format tanggal harus YYYY-MM-DD, contoh 2026-06-23.');
      return;
    }

    setLoading(true);
    try {
      await axios.post(
        `${API_BASE}/pesticides/use`,
        { pesticide_id: useId, qty: q, note: useNote, date: useDateFinal || null },
        { headers }
      );

      if (assignMode === 'fields' && selectedFields.length > 0) {
        const unitPrice = Number(selectedPesticide?.unit_price || 0);
        const total = q * unitPrice;

        if (!(unitPrice > 0) || !(total > 0)) {
          Alert.alert(
            'Info',
            'Stok sudah dikurangi, tapi biaya ke lahan belum dibuat karena harga produk belum ada.'
          );
        } else {
          await axios.post(
            `${API_BASE}/field-expenses/split`,
            {
              expense_date: useDateFinal || new Date().toISOString().slice(0, 10),
              category: inventoryExpenseCategory(selectedPesticide),
              subcategory: inventoryExpenseSubcategory(selectedPesticide),
              item_name: selectedPesticide?.name || null,
              quantity: q,
              unit: selectedPesticide?.unit || null,
              unit_price: unitPrice,
              total,
              notes: useNote || null,
              pesticide_id: useId,
              field_ids: selectedFields,
              split_method: splitMethod,
            },
            { headers }
          );
        }
      }

      setUseOpen(false);
      await fetchList();
      Alert.alert('Sukses', `Stok berkurang ${q}`);
    } catch (err) {
      Alert.alert('Error', err?.response?.data?.error || err?.message || 'Gagal update terpakai');
    } finally {
      setLoading(false);
    }
  };

  const openPrint = (item) => {
    setPrintTarget(item);
    setPrintSize(50);
    setPrintModalOpen(true);
  };

  const doPrintNow = async () => {
    const p = printTarget;
    const widthMm = printSize || 50;
    setPrintModalOpen(false);
    if (!p) return;

    const url = `${API_BASE}/print/label?pesticide_id=${p.pesticide_id}&width_mm=${widthMm}&autoprint=1`;

    if (Platform.OS === 'web') {
      const w = window.open(url, '_blank', 'noopener');
      if (!w) Alert.alert('Print diblokir', 'Izinkan pop-up untuk situs ini, lalu ulangi “Print”.');
      return;
    }

    try {
      const resp = await fetch(url, { cache: 'no-store' });
      if (!resp.ok) throw new Error(`fetch gagal: ${resp.status} ${resp.statusText}`);
      const html = await resp.text();
      const fixed = injectAutoPrintScript(absolutizeHtml(html));
      await Print.printAsync({ html: fixed });
      return;
    } catch (e1) {
      log('native html error:', e1?.message || e1);
    }

    try {
      await Print.printAsync({ uri: url });
      return;
    } catch (e2) {
      log('native uri error:', e2?.message || e2);
    }

    try {
      const resp2 = await fetch(url);
      const html2 = await resp2.text();
      const { uri } = await Print.printToFileAsync({ html: html2 });
      await Sharing.shareAsync(uri);
    } catch (e3) {
      log('printToFile/share error:', e3?.message || e3);
      Alert.alert('Print', e3?.message || 'Gagal mencetak / membagikan PDF.');
    }
  };

  const stockLevel = (n) => {
    const v = Number(n) || 0;
    if (v <= 3) return 'low';
    if (v <= 10) return 'mid';
    return 'high';
  };

  const StockBadge = ({ stock, unit }) => {
    const lvl = stockLevel(stock);
    return (
      <View
        style={[
          styles.stockBadge,
          { minWidth: STOCK_BADGE_MIN_W, paddingVertical: STOCK_BADGE_PV },
          lvl === 'low' && styles.stockLow,
          lvl === 'mid' && styles.stockMid,
          lvl === 'high' && styles.stockHigh,
        ]}
      >
        <Text
          style={[
            styles.stockBadgeNum,
            { fontSize: STOCK_NUM_SIZE },
            lvl === 'low' && styles.stockLowText,
            lvl === 'mid' && styles.stockMidText,
            lvl === 'high' && styles.stockHighText,
          ]}
        >
          {Number(stock) || 0}
        </Text>
        <Text
          style={[
            styles.stockBadgeUnit,
            { fontSize: STOCK_UNIT_SIZE },
            lvl === 'low' && styles.stockLowText,
            lvl === 'mid' && styles.stockMidText,
            lvl === 'high' && styles.stockHighText,
          ]}
          numberOfLines={1}
        >
          {unit || ''}
        </Text>
      </View>
    );
  };

  const StockBar = ({ stock }) => {
    const v = Math.max(0, Number(stock) || 0);
    const pct = Math.min(1, v / 30);
    const lvl = stockLevel(v);
    return (
      <View style={styles.barWrap}>
        <View style={styles.barBg}>
          <View
            style={[
              styles.barFill,
              { width: `${Math.round(pct * 100)}%` },
              lvl === 'low' && styles.barLow,
              lvl === 'mid' && styles.barMid,
              lvl === 'high' && styles.barHigh,
            ]}
          />
        </View>
        <Text style={styles.barHint}>
          {lvl === 'low' ? 'Stok menipis' : lvl === 'mid' ? 'Stok sedang' : 'Stok aman'}
        </Text>
      </View>
    );
  };

  const Pill = ({ title, icon, onPress, type = 'primary' }) => {
    const primary = type === 'primary';
    return (
      <TouchableOpacity
        onPress={onPress}
        activeOpacity={0.9}
        style={[styles.pill, primary ? styles.pillPrimary : styles.pillOutline]}
      >
        {icon ? (
          <Ionicons
            name={icon}
            size={16}
            color={primary ? '#fff' : PADIPRO.primary}
            style={{ marginRight: 6 }}
          />
        ) : null}
        <Text style={[styles.pillText, primary ? { color: '#fff' } : { color: PADIPRO.primary }]}>
          {title}
        </Text>
      </TouchableOpacity>
    );
  };

  const Chip = ({ label, active, onPress, color = PADIPRO.primary }) => (
    <TouchableOpacity
      onPress={onPress}
      activeOpacity={0.9}
      style={[styles.chip, active ? { backgroundColor: PADIPRO.primarySoft, borderColor: color } : null]}
    >
      <Text style={[styles.chipText, active ? { color } : null]} numberOfLines={1}>
        {label}
      </Text>
    </TouchableOpacity>
  );

  const ItemCard = ({ item }) => {
    const stock = Number(item.stock || 0);
    const unitPrice = Number(item.unit_price || 0);
    const stockValue = stock > 0 && unitPrice > 0 ? stock * unitPrice : 0;
    const hasPrice = unitPrice > 0;
    const isFertilizer = isFertilizerCategory(item.category);

    return (
      <View style={[styles.card, isFertilizer && styles.cardFertilizer]}>
        <View style={styles.cardTop}>
          <View style={{ flex: 1, paddingRight: 10 }}>
            <View style={styles.kindRow}>
              <View style={[styles.kindBadge, isFertilizer ? styles.kindBadgeFertilizer : styles.kindBadgePesticide]}>
                <Ionicons
                  name={isFertilizer ? 'nutrition-outline' : 'leaf-outline'}
                  size={12}
                  color={isFertilizer ? PADIPRO.goldDeep : PADIPRO.primary}
                />
                <Text style={[styles.kindBadgeText, isFertilizer ? styles.kindBadgeTextFertilizer : styles.kindBadgeTextPesticide]}>
                  {inventoryKindLabel(item)}
                </Text>
              </View>
            </View>
            <Text style={styles.itemTitle} numberOfLines={isHandset ? 2 : 1}>
              {item.name}
            </Text>

            {item.category || item.company ? (
              <View style={styles.metaRow}>
                {item.category ? (
                  <View style={[styles.metaPill, isFertilizer && styles.metaPillFertilizer]}>
                    <Ionicons name="pricetag-outline" size={12} color={isFertilizer ? PADIPRO.goldDeep : PADIPRO.muted} />
                    <Text style={[styles.metaText, isFertilizer && styles.metaTextFertilizer]}>{item.category}</Text>
                  </View>
                ) : null}
                {item.company ? (
                  <View style={styles.metaPill}>
                    <Ionicons name="business-outline" size={12} color={PADIPRO.muted} />
                    <Text style={styles.metaText}>{item.company}</Text>
                  </View>
                ) : null}
              </View>
            ) : (
              <Text style={styles.metaGhost}>Tanpa kategori/perusahaan</Text>
            )}

            <StockBar stock={item.stock} />
          </View>

          <StockBadge stock={item.stock} unit={item.unit} />
        </View>

        <View style={[styles.valueStrip, isHandset && styles.valueStripStacked]}>
          <View style={styles.valueStripLeft}>
            <Ionicons name="wallet-outline" size={15} color={PADIPRO.primary} />
            <View>
              <Text style={styles.valueStripLabel}>Nilai stok</Text>
              <Text style={styles.valueStripFormula} numberOfLines={1}>
                {stock} {item.unit || 'unit'} × {hasPrice ? formatIDR(unitPrice) : 'harga belum diisi'}
              </Text>
            </View>
          </View>
          <Text style={[styles.valueStripAmount, isHandset && styles.valueStripAmountHandset, !hasPrice && styles.valueStripAmountMuted]}>
            {hasPrice ? formatIDR(stockValue) : 'Belum lengkap'}
          </Text>
        </View>

        <View style={styles.actionsRow}>
          <Pill title="Terpakai" icon="remove" type="outline" onPress={() => openUse(item.pesticide_id)} />
          <Pill title="Restock" icon="add" type="outline" onPress={() => openRestock(item.pesticide_id)} />
          <Pill title="Harga" icon="cash-outline" type="outline" onPress={() => openPriceModal(item)} />
          <Pill title="Print" icon="print" type="primary" onPress={() => openPrint(item)} />
        </View>
      </View>
    );
  };

  const ListHeader = useCallback(
    () => (
      <View style={{ marginBottom: 8 }}>
        <View>
          <View style={styles.stockValuePanel}>
            <View style={styles.stockValueHead}>
              <View>
                <Text style={styles.stockValueEyebrow}>Nilai Stok Sarana Produksi</Text>
                <Text style={styles.stockValueTitle}>{formatIDR(inventorySummary.totalValue)}</Text>
              </View>
              <View style={styles.stockValueIcon}>
                <Ionicons name="stats-chart" size={18} color='#FFFFFF' />
              </View>
            </View>
            <Text style={styles.stockValueDesc}>
              Aset pupuk dan pestisida. Belum jadi biaya lahan sampai barang dipakai.
            </Text>
            <View style={styles.stockValueMetrics}>
              <View style={styles.stockMetric}>
                <Text style={styles.stockMetricLabel}>Item</Text>
                <Text style={styles.stockMetricValue}>{inventorySummary.itemCount}</Text>
              </View>
              <View style={styles.stockMetric}>
                <Text style={styles.stockMetricLabel}>Total Unit</Text>
                <Text style={styles.stockMetricValue}>{inventorySummary.totalStock}</Text>
              </View>
              <View style={styles.stockMetric}>
                <Text style={styles.stockMetricLabel}>Harga Kosong</Text>
                <Text style={[styles.stockMetricValue, inventorySummary.missingPrice > 0 && styles.stockMetricWarn]}>
                  {inventorySummary.missingPrice}
                </Text>
              </View>
            </View>
            {inventorySummary.topItem ? (
              <View style={styles.stockValueInsight}>
                <Text style={styles.stockValueInsightText} numberOfLines={1}>
                  Tertinggi: {inventorySummary.topItem.name} • {formatIDR(inventorySummary.topItem.value)}
                </Text>
              </View>
            ) : null}
          </View>

          <Text style={styles.filterTitle}>Kategori</Text>
          <ScrollView
            horizontal
            showsHorizontalScrollIndicator={false}
            contentContainerStyle={styles.chipsRow}
            keyboardShouldPersistTaps="handled"
          >
            {categories.map((cat) => (
              <Chip
                key={cat}
                label={cat}
                active={selectedCategory === cat}
                onPress={() => setSelectedCategory(cat)}
              />
            ))}
          </ScrollView>

          <Text style={[styles.filterTitle, { marginTop: 10 }]}>Perusahaan</Text>
          <ScrollView
            horizontal
            showsHorizontalScrollIndicator={false}
            contentContainerStyle={styles.chipsRow}
            keyboardShouldPersistTaps="handled"
          >
            {companies.map((com) => (
              <Chip
                key={com}
                label={com}
                active={selectedCompany === com}
                onPress={() => setSelectedCompany(com)}
              />
            ))}
          </ScrollView>

          <View style={styles.headerActions}>
            <View style={styles.legendRow}>
              <View style={[styles.legendDot, styles.legendLow]} />
              <Text style={styles.legendText}>Menipis</Text>
              <View style={[styles.legendDot, styles.legendMid]} />
              <Text style={styles.legendText}>Sedang</Text>
              <View style={[styles.legendDot, styles.legendHigh]} />
              <Text style={styles.legendText}>Aman</Text>
            </View>

            <TouchableOpacity onPress={resetFilters} style={styles.resetBtn} activeOpacity={0.9}>
              <Ionicons name="refresh" size={14} color={PADIPRO.textStrong} />
              <Text style={{ marginLeft: 6, color: PADIPRO.textStrong, fontWeight: '700' }}>Reset</Text>
            </TouchableOpacity>
          </View>
        </View>
      </View>
    ),
    [categories, companies, selectedCategory, selectedCompany, inventorySummary]
  );

  return (
    <SafeAreaView style={{ flex: 1, backgroundColor: PADIPRO.bg }}>
      <View style={[styles.screenInner, { padding: Math.max(10, contentPadding - 2) }, contentWidthStyle]}>
        {!embedded && (
          <View style={{ marginBottom: 10 }}>
            <EnterpriseHeader
              title="Inventori Sarana Produksi"
              subtitle={`${filtered.length} item • ${selectedCategory === 'All' ? 'Semua Kategori' : selectedCategory} • ${selectedCompany === 'All' ? 'Semua Perusahaan' : selectedCompany}`}
              right={(
                <TouchableOpacity onPress={onRefresh} style={styles.headerActionBtn} activeOpacity={0.85}>
                  <Ionicons name="refresh" size={16} color='#FFFFFF' />
                </TouchableOpacity>
              )}
            />
          </View>
        )}

        <FlatList
          data={filtered}
          keyExtractor={(i) => i.pesticide_id.toString()}
          renderItem={({ item }) => <ItemCard item={item} />}
          ListHeaderComponent={ListHeader}
          ListEmptyComponent={
            <View style={styles.emptyWrap}>
              <Ionicons name="leaf-outline" size={26} color={PADIPRO.mutedSoft} />
              <Text style={styles.emptyTitle}>Tidak ada data</Text>
              <Text style={styles.emptyText}>Coba ubah filter atau kata kunci pencarian.</Text>
              <TouchableOpacity
                onPress={resetFilters}
                style={[styles.resetBtn, { marginTop: 10 }]}
                activeOpacity={0.9}
              >
                <Ionicons name="funnel-outline" size={14} color={PADIPRO.textStrong} />
                <Text style={{ marginLeft: 6, color: PADIPRO.textStrong, fontWeight: '700' }}>
                  Bersihkan Filter
                </Text>
              </TouchableOpacity>
            </View>
          }
          refreshControl={<RefreshControl refreshing={refreshing} onRefresh={onRefresh} tintColor={PADIPRO.primary} />}
          keyboardShouldPersistTaps="handled"
          contentContainerStyle={{ paddingBottom: 18 }}
          contentInsetAdjustmentBehavior="automatic"
          showsVerticalScrollIndicator={false}
        />
      </View>

      {/* Modal Restock */}
      <Modal visible={restockOpen} transparent animationType="slide" onRequestClose={() => setRestockOpen(false)}>
        <View style={styles.modalBackdrop}>
          <KeyboardAvoidingView
            behavior={Platform.OS === 'ios' ? 'padding' : undefined}
            style={{ width: '100%' }}
          >
            <View style={[styles.modalSheet, contentWidthStyle]}>
              <Text style={styles.modalTitle}>Restock</Text>
              <Text style={styles.modalDesc}>Masukkan jumlah penambahan stok</Text>

              <Text style={styles.inputLabel}>Qty</Text>
              <TextInput
                value={qty}
                onChangeText={setQty}
                keyboardType={Platform.OS === 'ios' ? 'number-pad' : 'numeric'}
                style={styles.input}
                placeholder="1"
              />

              <Text style={styles.inputLabel}>Harga per Unit</Text>
              <TextInput
                value={restockUnitPrice}
                onChangeText={(t) => setRestockUnitPrice(String(t).replace(/[^0-9.]/g, ''))}
                keyboardType={Platform.OS === 'ios' ? 'number-pad' : 'numeric'}
                style={styles.input}
                placeholder="contoh: 75000"
              />
              <Text style={styles.modalHint}>
                Kosongkan kalau pakai harga lama. Nilai restock: {formatIDR((Number(qty) || 0) * (Number(restockUnitPrice || selectedRestockPesticide?.unit_price) || 0))}
              </Text>

              <Text style={styles.inputLabel}>Catatan (opsional)</Text>
              <TextInput
                value={note}
                onChangeText={setNote}
                style={styles.input}
                placeholder="contoh: tambah stok gudang A"
              />

              <View style={styles.modalActionsRow}>
                <TouchableOpacity
                  onPress={() => setRestockOpen(false)}
                  style={[styles.modalBtn, styles.modalCancel]}
                >
                  <Text style={styles.modalCancelText}>Batal</Text>
                </TouchableOpacity>

                <TouchableOpacity
                  onPress={doRestock}
                  disabled={loading}
                  style={[styles.modalBtn, styles.modalSave, loading && { opacity: 0.7 }]}
                >
                  {loading ? <ActivityIndicator color="#fff" /> : <Text style={styles.modalSaveText}>Simpan</Text>}
                </TouchableOpacity>
              </View>
            </View>
          </KeyboardAvoidingView>
        </View>
      </Modal>

      {/* Modal Edit Harga */}
      <Modal visible={priceOpen} transparent animationType="slide" onRequestClose={() => setPriceOpen(false)}>
        <View style={styles.modalBackdrop}>
          <KeyboardAvoidingView
            behavior={Platform.OS === 'ios' ? 'padding' : undefined}
            style={{ width: '100%' }}
          >
            <View style={[styles.modalSheet, contentWidthStyle]}>
              <Text style={styles.modalTitle}>Edit Harga per Unit</Text>
              <Text style={styles.modalDesc} numberOfLines={2}>
                {selectedPricePesticide?.name || 'Produk'} • dipakai untuk hitung nilai stok dan biaya lahan berikutnya.
              </Text>

              <Text style={styles.inputLabel}>Harga per Unit</Text>
              <TextInput
                value={priceInput}
                onChangeText={(t) => setPriceInput(String(t).replace(/[^0-9.]/g, ''))}
                keyboardType={Platform.OS === 'ios' ? 'number-pad' : 'numeric'}
                style={styles.input}
                placeholder="contoh: 75000"
              />
              <Text style={styles.modalHint}>
                Preview nilai stok: {formatIDR((Number(selectedPricePesticide?.stock) || 0) * (Number(priceInput) || 0))}
              </Text>

              <View style={styles.modalActionsRow}>
                <TouchableOpacity
                  onPress={() => setPriceOpen(false)}
                  style={[styles.modalBtn, styles.modalCancel]}
                >
                  <Text style={styles.modalCancelText}>Batal</Text>
                </TouchableOpacity>

                <TouchableOpacity
                  onPress={doUpdatePrice}
                  disabled={loading}
                  style={[styles.modalBtn, styles.modalSave, loading && { opacity: 0.7 }]}
                >
                  {loading ? <ActivityIndicator color="#fff" /> : <Text style={styles.modalSaveText}>Simpan</Text>}
                </TouchableOpacity>
              </View>
            </View>
          </KeyboardAvoidingView>
        </View>
      </Modal>

      {/* Modal Terpakai */}
      <Modal visible={useOpen} transparent animationType="slide" onRequestClose={() => setUseOpen(false)}>
        <View style={styles.modalBackdrop}>
          <KeyboardAvoidingView
            behavior={Platform.OS === 'ios' ? 'padding' : undefined}
            style={{ width: '100%' }}
          >
            <View style={[styles.modalSheet, styles.useModalSheet, contentWidthStyle, isHandset && styles.useModalSheetCompact]}>
              <ScrollView
                showsVerticalScrollIndicator={false}
                keyboardShouldPersistTaps="handled"
                contentContainerStyle={styles.modalScrollContent}
              >
                <View style={styles.sheetHandle} />
                <View style={styles.useModalHeader}>
                  <View style={{ flex: 1, minWidth: 0 }}>
                    <Text style={styles.modalEyebrow}>Inventory Usage</Text>
                    <Text style={styles.modalTitle}>Barang Terpakai</Text>
                    <Text style={styles.modalDesc}>
                      Kurangi stok dan langsung masuk biaya operasional.
                    </Text>
                  </View>
                  <TouchableOpacity
                    onPress={() => setUseOpen(false)}
                    activeOpacity={0.85}
                    style={styles.useCloseBtn}
                  >
                    <Ionicons name="close" size={18} color={PADIPRO.textStrong} />
                  </TouchableOpacity>
                </View>

                {!!selectedPesticide && (
                  <View style={styles.useHeroCard}>
                    <View style={styles.useHeroTop}>
                      <View style={{ flex: 1, minWidth: 0 }}>
                        <Text style={styles.useHeroLabel}>{inventoryKindLabel(selectedPesticide)} terpakai</Text>
                        <Text style={styles.useHeroTitle} numberOfLines={1}>{selectedPesticide.name}</Text>
                      </View>
                      <View style={styles.useHeroStockBadge}>
                        <Text style={styles.useHeroStockNum}>{selectedPesticide.stock || 0}</Text>
                        <Text style={styles.useHeroStockUnit} numberOfLines={1}>{selectedPesticide.unit || 'unit'}</Text>
                      </View>
                    </View>
                    <View style={styles.useMetricGrid}>
                      <View style={styles.useMetricCard}>
                        <Text style={styles.useMetricLabel}>Harga</Text>
                        <Text style={styles.useMetricValue}>
                          {useUnitPrice > 0 ? formatIDR(useUnitPrice) : 'Belum ada'}
                        </Text>
                      </View>
                      <View style={styles.useMetricCard}>
                        <Text style={styles.useMetricLabel}>Biaya Pakai</Text>
                        <Text style={styles.useMetricValue}>{formatIDR(useTotalPreview || 0)}</Text>
                      </View>
                      <View style={styles.useMetricCard}>
                        <Text style={styles.useMetricLabel}>Sisa Stok</Text>
                        <Text style={styles.useMetricValue}>{useStockAfter} {selectedPesticide.unit || ''}</Text>
                      </View>
                    </View>
                    {useUnitPrice <= 0 ? (
                      <Text style={styles.useHeroWarn}>
                        Isi harga dulu lewat tombol Harga agar biaya lahan bisa dihitung.
                      </Text>
                    ) : null}
                  </View>
                )}

                <View style={styles.useFormGrid}>
                  <View style={styles.useFormCol}>
                    <Text style={styles.inputLabel}>Tanggal</Text>
                    <TextInput
                      value={useDate}
                      onChangeText={setUseDate}
                      keyboardType={Platform.OS === 'ios' ? 'numbers-and-punctuation' : 'default'}
                      style={styles.input}
                      placeholder="YYYY-MM-DD"
                    />
                  </View>
                  <View style={styles.useFormCol}>
                    <Text style={styles.inputLabel}>Qty Pakai</Text>
                    <TextInput
                      value={useQty}
                      onChangeText={(t) => setUseQty(String(t).replace(/[^0-9.]/g, ''))}
                      keyboardType={Platform.OS === 'ios' ? 'number-pad' : 'numeric'}
                      style={styles.input}
                      placeholder="1"
                    />
                  </View>
                </View>
                <Text style={styles.modalHint}>
                  Tanggal ini dipakai untuk histori stok dan biaya lahan di Operasional.
                </Text>

                <Text style={styles.inputLabel}>Catatan (opsional)</Text>
                <TextInput
                  value={useNote}
                  onChangeText={setUseNote}
                  style={styles.input}
                  placeholder="contoh: mupuk blok A / semprot blok A"
                />

                <Text style={styles.inputLabel}>Alokasi</Text>
                <View style={styles.rowWrap}>
                  <Chip
                    label="Stok saja"
                    active={assignMode === 'general'}
                    onPress={() => {
                      setAssignMode('general');
                      setSelectedFields([]);
                    }}
                  />
                  <Chip
                    label="Biaya ke Lahan"
                    active={assignMode === 'fields'}
                    onPress={() => setAssignMode('fields')}
                  />
                </View>

                {assignMode === 'fields' && (
                  <View style={styles.allocBox}>
                    <View style={styles.allocHeader}>
                      <View style={{ flex: 1, minWidth: 0 }}>
                        <Text style={styles.allocTitle}>Pilih Lahan</Text>
                        <Text style={styles.allocSubTitle}>
                          {selectedFields.length} dipilih
                          {selectedAreaTotal > 0 ? ` • ${selectedAreaTotal.toLocaleString('id-ID')} rante` : ''}
                        </Text>
                      </View>
                      <TouchableOpacity
                        onPress={fetchFieldsCompat}
                        style={styles.refreshMiniBtn}
                        activeOpacity={0.9}
                      >
                        {fieldsLoading ? (
                          <ActivityIndicator size="small" />
                        ) : (
                          <Ionicons name="refresh" size={14} color={PADIPRO.primary} />
                        )}
                        <Text style={styles.refreshMiniText}>Refresh</Text>
                      </TouchableOpacity>
                    </View>

                    {fieldsLoading ? (
                      <View style={{ paddingVertical: 10 }}>
                        <ActivityIndicator />
                      </View>
                    ) : fieldsList.length === 0 ? (
                      <View style={styles.emptyFields}>
                        <Text style={styles.emptyFieldsTitle}>Belum ada data lahan</Text>
                        <Text style={styles.emptyFieldsText}>
                          Pastikan endpoint /operations/fields tersedia dan mengembalikan field aktif.
                        </Text>
                      </View>
                    ) : (
                      <>
                        <View style={styles.allocToolsRow}>
                          <TouchableOpacity
                            onPress={allFieldsSelected ? resetFieldPick : pickAllFields}
                            activeOpacity={0.9}
                            style={styles.allocToolBtn}
                          >
                            <Ionicons
                              name={allFieldsSelected ? 'close-circle-outline' : 'checkmark-done-circle-outline'}
                              size={14}
                              color={PADIPRO.primary}
                            />
                            <Text style={styles.allocToolText}>{allFieldsSelected ? 'Reset' : 'Pilih Semua'}</Text>
                          </TouchableOpacity>
                          <TouchableOpacity onPress={resetFieldPick} activeOpacity={0.9} style={styles.allocToolBtnGhost}>
                            <Text style={styles.allocToolGhostText}>Kosongkan</Text>
                          </TouchableOpacity>
                        </View>

                        <View style={styles.allocMethodCard}>
                          <Text style={styles.allocMethodTitle}>Pembagian biaya</Text>
                          <View style={styles.rowWrap}>
                            <Chip
                              label="Berdasarkan Luas"
                              active={splitMethod === 'area'}
                              onPress={() => setSplitMethod('area')}
                            />
                            <Chip
                              label="Rata"
                              active={splitMethod === 'equal'}
                              onPress={() => setSplitMethod('equal')}
                            />
                          </View>
                          <Text style={styles.methodHint}>
                            Default luas/rante. Kalau rante belum lengkap, sistem otomatis fallback rata.
                          </Text>
                        </View>

                        <ScrollView
                          style={[styles.fieldsListBox, isHandset && styles.fieldsListBoxHandset]}
                          nestedScrollEnabled
                          showsVerticalScrollIndicator
                          keyboardShouldPersistTaps="handled"
                        >
                          {fieldsList.map((f) => {
                            const checked = selectedFields.includes(f.id);
                            const preview = splitPreview.find((x) => x.id === f.id);
                            return (
                              <TouchableOpacity
                                key={f.id}
                                style={[styles.fieldRow, checked && styles.fieldRowActive]}
                                onPress={() => toggleFieldPick(f.id)}
                                activeOpacity={0.9}
                              >
                                <View style={{ flex: 1 }}>
                                  <Text style={styles.fieldName}>{f.name}</Text>
                                  <Text style={styles.fieldMeta}>
                                    {f.area_rante != null ? `${f.area_rante} rante` : 'rante belum diisi'}
                                    {preview ? ` • ${formatIDR(preview.amount)}` : ''}
                                  </Text>
                                </View>
                                <Ionicons
                                  name={checked ? 'checkmark-circle' : 'ellipse-outline'}
                                  size={22}
                                  color={checked ? PADIPRO.primary : PADIPRO.mutedSoft}
                                />
                              </TouchableOpacity>
                            );
                          })}
                        </ScrollView>

                        <View style={styles.previewBox}>
                          <Text style={styles.previewTitle}>Preview pembagian</Text>
                          <Text style={styles.previewText}>
                            {selectedFields.length} lahan dipilih • Total: <Text style={styles.previewStrong}>{formatIDR(useTotalPreview || 0)}</Text>
                          </Text>

                          {splitPreview.length > 0 && (
                            <View style={{ marginTop: 8 }}>
                              {splitPreview.slice(0, 5).map((x) => (
                                <View key={x.id} style={styles.splitRow}>
                                  <Text style={styles.splitName}>{x.name}</Text>
                                  <Text style={styles.splitAmt}>{formatIDR(x.amount)}</Text>
                                </View>
                              ))}
                              {splitPreview.length > 5 ? (
                                <Text style={styles.splitMoreText}>
                                  +{splitPreview.length - 5} lahan lain tetap ikut dihitung
                                </Text>
                              ) : null}
                            </View>
                          )}

                          {!selectedPesticide?.unit_price ? (
                            <Text style={styles.previewWarn}>
                              Harga produk belum ada. Stok tetap bisa berkurang, tapi biaya ke lahan tidak
                              bisa dihitung.
                            </Text>
                          ) : null}
                        </View>
                      </>
                    )}
                  </View>
                )}
              </ScrollView>

              <View style={styles.modalFooter}>
                <View style={styles.footerSummary}>
                  <Text style={styles.footerSummaryLabel}>
                    {assignMode === 'fields' ? `${selectedFields.length} lahan` : 'Stok saja'}
                  </Text>
                  <Text style={styles.footerSummaryValue}>{formatIDR(useTotalPreview || 0)}</Text>
                </View>
                <TouchableOpacity onPress={() => setUseOpen(false)} style={[styles.modalBtn, styles.modalCancel]}>
                  <Text style={styles.modalCancelText}>Batal</Text>
                </TouchableOpacity>

                <TouchableOpacity
                  onPress={doUse}
                  disabled={useSubmitDisabled}
                  style={[styles.modalBtn, styles.modalSave, useSubmitDisabled && { opacity: 0.55 }]}
                >
                  {loading ? <ActivityIndicator color="#fff" /> : <Text style={styles.modalSaveText}>Simpan Pakai</Text>}
                </TouchableOpacity>
              </View>
            </View>
          </KeyboardAvoidingView>
        </View>
      </Modal>

      {/* Modal Print */}
      <Modal visible={printModalOpen} transparent animationType="fade" onRequestClose={() => setPrintModalOpen(false)}>
        <View style={styles.modalBackdropCenter}>
          <View style={[styles.printCard, contentWidthStyle]}>
            <Text style={styles.printTitle}>Cetak Label</Text>
            <Text style={styles.printSub} numberOfLines={2}>
              {printTarget?.name || 'Pilih ukuran label'}
            </Text>

            <Text style={[styles.inputLabel, { marginTop: 10 }]}>Ukuran Lebar Kertas</Text>
            <View style={styles.sizeRow}>
              {PRINT_SIZES.map((sz) => {
                const active = printSize === sz;
                return (
                  <TouchableOpacity
                    key={sz}
                    onPress={() => setPrintSize(sz)}
                    activeOpacity={0.9}
                    style={[styles.sizeChip, active && styles.sizeChipActive]}
                  >
                    <Text style={[styles.sizeChipText, active && styles.sizeChipTextActive]}>{sz} mm</Text>
                  </TouchableOpacity>
                );
              })}
            </View>

            <View style={styles.modalActionsRow}>
              <TouchableOpacity
                onPress={() => setPrintModalOpen(false)}
                style={[styles.modalBtn, styles.modalCancel]}
              >
                <Text style={styles.modalCancelText}>Batal</Text>
              </TouchableOpacity>
              <TouchableOpacity onPress={doPrintNow} style={[styles.modalBtn, styles.modalSave]}>
                <Text style={styles.modalSaveText}>Cetak</Text>
              </TouchableOpacity>
            </View>

            <Text style={styles.hint}>Tip: Untuk web, izinkan pop-up agar cetak otomatis berjalan lancar.</Text>
          </View>
        </View>
      </Modal>
    </SafeAreaView>
  );
}

const styles = StyleSheet.create({
  screenInner: {
    flex: 1,
  },

  headerActionBtn: {
    padding: 8,
    borderRadius: 999,
    backgroundColor: 'rgba(255,255,255,0.16)',
    borderWidth: 1,
    borderColor: 'rgba(255,255,255,0.32)',
  },

  filterTitle: { fontWeight: '800', color: PADIPRO.text, marginBottom: 6, marginTop: 8 },
  chipsRow: { gap: 8, paddingRight: 4 },

  chip: {
    paddingVertical: 8,
    paddingHorizontal: 12,
    borderRadius: 999,
    borderWidth: 1,
    borderColor: PADIPRO.border,
    backgroundColor: PADIPRO.surface,
    marginRight: 8,
    marginBottom: 8,
  },
  chipText: { color: PADIPRO.text, fontSize: 12, fontWeight: '800' },

  headerActions: {
    marginTop: 10,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 10,
  },

  resetBtn: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: PADIPRO.surface,
    borderWidth: 1,
    borderColor: PADIPRO.border,
    paddingVertical: 8,
    paddingHorizontal: 12,
    borderRadius: 12,
  },

  legendRow: { flexDirection: 'row', alignItems: 'center', gap: 6, flexWrap: 'wrap' },
  legendDot: { width: 10, height: 10, borderRadius: 5 },
  legendLow: { backgroundColor: PADIPRO.danger },
  legendMid: { backgroundColor: PADIPRO.warning },
  legendHigh: { backgroundColor: PADIPRO.primary },
  legendText: { color: PADIPRO.muted, fontWeight: '700', marginRight: 8, fontSize: 12 },

  stockValuePanel: {
    borderWidth: 1,
    borderColor: PADIPRO.borderStrong,
    borderRadius: 18,
    backgroundColor: PADIPRO.primarySoft,
    padding: 13,
    marginBottom: 12,
    ...padiproShadow,
  },
  stockValueHead: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 10,
  },
  stockValueEyebrow: {
    color: PADIPRO.muted,
    fontSize: 11,
    fontWeight: '900',
    letterSpacing: 0.5,
    textTransform: 'uppercase',
  },
  stockValueTitle: {
    marginTop: 3,
    color: PADIPRO.textStrong,
    fontSize: 21,
    fontWeight: '900',
  },
  stockValueIcon: {
    width: 38,
    height: 38,
    borderRadius: 14,
    backgroundColor: PADIPRO.primary,
    alignItems: 'center',
    justifyContent: 'center',
  },
  stockValueDesc: {
    marginTop: 7,
    color: PADIPRO.muted,
    fontSize: 12,
    fontWeight: '700',
    lineHeight: 17,
  },
  stockValueMetrics: {
    marginTop: 10,
    flexDirection: 'row',
    gap: 8,
  },
  stockMetric: {
    flex: 1,
    borderWidth: 1,
    borderColor: PADIPRO.border,
    backgroundColor: PADIPRO.surface,
    borderRadius: 13,
    paddingHorizontal: 9,
    paddingVertical: 8,
  },
  stockMetricLabel: {
    color: PADIPRO.muted,
    fontSize: 10,
    fontWeight: '900',
    textTransform: 'uppercase',
  },
  stockMetricValue: {
    marginTop: 3,
    color: PADIPRO.textStrong,
    fontSize: 14,
    fontWeight: '900',
  },
  stockMetricWarn: { color: PADIPRO.warning },
  stockValueInsight: {
    marginTop: 9,
    borderTopWidth: 1,
    borderTopColor: PADIPRO.border,
    paddingTop: 8,
  },
  stockValueInsightText: {
    color: PADIPRO.textStrong,
    fontSize: 12,
    fontWeight: '800',
  },

  card: {
    backgroundColor: PADIPRO.surface,
    borderRadius: 18,
    borderWidth: 1,
    borderColor: PADIPRO.border,
    padding: 12,
    marginBottom: 12,
    ...padiproShadow,
  },
  cardFertilizer: {
    borderColor: `${PADIPRO.gold}40`,
    backgroundColor: PADIPRO.goldSoft,
  },

  cardTop: { flexDirection: 'row', alignItems: 'flex-start' },
  kindRow: { flexDirection: 'row', marginBottom: 7 },
  kindBadge: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 5,
    borderWidth: 1,
    borderRadius: 999,
    paddingHorizontal: 9,
    paddingVertical: 4,
  },
  kindBadgePesticide: {
    borderColor: PADIPRO.primarySoftStrong,
    backgroundColor: PADIPRO.primarySoft,
  },
  kindBadgeFertilizer: {
    borderColor: `${PADIPRO.gold}40`,
    backgroundColor: PADIPRO.goldSoft,
  },
  kindBadgeText: {
    fontSize: 10,
    fontWeight: '900',
    textTransform: 'uppercase',
    letterSpacing: 0.4,
  },
  kindBadgeTextPesticide: { color: PADIPRO.primary },
  kindBadgeTextFertilizer: { color: PADIPRO.goldDeep },
  itemTitle: { fontWeight: '900', fontSize: 16, color: PADIPRO.text },

  metaRow: { flexDirection: 'row', flexWrap: 'wrap', gap: 8, marginTop: 8 },
  metaPill: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
    paddingVertical: 6,
    paddingHorizontal: 10,
    borderRadius: 999,
    backgroundColor: PADIPRO.surfaceSunken,
    borderWidth: 1,
    borderColor: PADIPRO.border,
  },
  metaPillFertilizer: {
    backgroundColor: PADIPRO.goldSoft,
    borderColor: `${PADIPRO.gold}40`,
  },
  metaText: { color: PADIPRO.muted, fontSize: 12, fontWeight: '700' },
  metaTextFertilizer: { color: PADIPRO.goldDeep },
  metaGhost: { marginTop: 8, color: PADIPRO.muted, fontWeight: '700', fontSize: 12 },

  stockBadge: {
    minWidth: 88,
    paddingVertical: 10,
    paddingHorizontal: 10,
    borderRadius: 16,
    borderWidth: 1,
    alignItems: 'center',
    justifyContent: 'center',
  },
  stockBadgeNum: { fontSize: 22, fontWeight: '900' },
  stockBadgeUnit: { marginTop: 2, fontSize: 12, fontWeight: '800' },

  stockLow: { backgroundColor: PADIPRO.dangerSoft, borderColor: PADIPRO.danger },
  stockMid: { backgroundColor: PADIPRO.warningSoft, borderColor: PADIPRO.warning },
  stockHigh: { backgroundColor: PADIPRO.primarySoft, borderColor: PADIPRO.primarySoftStrong },

  stockLowText: { color: PADIPRO.danger },
  stockMidText: { color: PADIPRO.warning },
  stockHighText: { color: PADIPRO.primary },

  barWrap: { marginTop: 10 },
  barBg: { height: 10, borderRadius: 999, backgroundColor: PADIPRO.border, overflow: 'hidden' },
  barFill: { height: 10, borderRadius: 999, backgroundColor: PADIPRO.primary },
  barLow: { backgroundColor: PADIPRO.danger },
  barMid: { backgroundColor: PADIPRO.warning },
  barHigh: { backgroundColor: PADIPRO.primary },
  barHint: { marginTop: 6, color: PADIPRO.muted, fontWeight: '700', fontSize: 12 },

  valueStrip: {
    marginTop: 12,
    borderWidth: 1,
    borderColor: PADIPRO.border,
    backgroundColor: PADIPRO.surface,
    borderRadius: 14,
    paddingHorizontal: 10,
    paddingVertical: 9,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 10,
  },
  valueStripStacked: {
    alignItems: 'flex-start',
    flexDirection: 'column',
  },
  valueStripLeft: {
    flex: 1,
    minWidth: 0,
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
  },
  valueStripLabel: {
    color: PADIPRO.textStrong,
    fontSize: 11,
    fontWeight: '900',
    textTransform: 'uppercase',
    letterSpacing: 0.3,
  },
  valueStripFormula: {
    marginTop: 2,
    color: PADIPRO.muted,
    fontSize: 11,
    fontWeight: '700',
  },
  valueStripAmount: {
    color: PADIPRO.textStrong,
    fontSize: 13,
    fontWeight: '900',
    textAlign: 'right',
  },
  valueStripAmountHandset: {
    textAlign: 'left',
    maxWidth: '100%',
  },
  valueStripAmountMuted: {
    color: PADIPRO.warning,
    fontSize: 11,
  },

  actionsRow: { flexDirection: 'row', flexWrap: 'wrap', gap: 7, marginTop: 12 },

  pill: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    borderRadius: 999,
    paddingVertical: 10,
    paddingHorizontal: 9,
    flex: 1,
    minWidth: '47%',
  },
  pillPrimary: { backgroundColor: PADIPRO.primary },
  pillOutline: { backgroundColor: PADIPRO.surface, borderWidth: 1, borderColor: PADIPRO.primary },
  pillText: { fontWeight: '900', fontSize: 12 },

  emptyWrap: { alignItems: 'center', paddingVertical: 36, paddingHorizontal: 12 },
  emptyTitle: { marginTop: 6, color: PADIPRO.muted, fontWeight: '900' },
  emptyText: { marginTop: 2, color: PADIPRO.muted, fontWeight: '700' },

  modalBackdrop: {
    flex: 1,
    backgroundColor: 'rgba(15,23,42,.45)',
    justifyContent: 'flex-end',
  },
  modalSheet: {
    backgroundColor: PADIPRO.surface,
    padding: 16,
    borderTopLeftRadius: 22,
    borderTopRightRadius: 22,
    maxHeight: '92%',
  },
  useModalSheet: {
    paddingHorizontal: 14,
    paddingTop: 8,
    paddingBottom: 10,
    borderTopLeftRadius: 28,
    borderTopRightRadius: 28,
    backgroundColor: PADIPRO.surface,
    maxHeight: '94%',
  },
  useModalSheetCompact: {
    maxHeight: '96%',
  },
  sheetHandle: {
    alignSelf: 'center',
    width: 42,
    height: 5,
    borderRadius: 999,
    backgroundColor: PADIPRO.border,
    marginBottom: 10,
  },
  useModalHeader: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    gap: 10,
  },
  modalEyebrow: {
    color: PADIPRO.muted,
    fontSize: 10,
    fontWeight: '900',
    letterSpacing: 0.7,
    textTransform: 'uppercase',
  },
  useCloseBtn: {
    width: 34,
    height: 34,
    borderRadius: 17,
    alignItems: 'center',
    justifyContent: 'center',
    borderWidth: 1,
    borderColor: PADIPRO.border,
    backgroundColor: PADIPRO.surface,
  },
  modalTitle: { fontWeight: '900', fontSize: 18, color: PADIPRO.text },
  modalDesc: { color: PADIPRO.muted, marginTop: 4, fontWeight: '700', lineHeight: 18, fontSize: 12 },

  inputLabel: { marginTop: 12, fontWeight: '800', color: PADIPRO.text },
  input: {
    borderWidth: 1,
    borderColor: PADIPRO.border,
    borderRadius: 14,
    padding: 12,
    marginTop: 8,
    backgroundColor: PADIPRO.surface,
  },
  useFormGrid: {
    flexDirection: 'row',
    gap: 10,
    marginTop: 2,
  },
  useFormCol: {
    flex: 1,
    minWidth: 0,
  },
  modalHint: {
    marginTop: 6,
    marginBottom: 2,
    color: PADIPRO.muted,
    fontSize: 12,
    fontWeight: '800',
  },

  useHeroCard: {
    marginTop: 10,
    borderWidth: 1,
    borderColor: PADIPRO.borderStrong,
    backgroundColor: PADIPRO.primarySoft,
    borderRadius: 18,
    padding: 11,
  },
  useHeroTop: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 10,
  },
  useHeroLabel: {
    color: PADIPRO.muted,
    fontSize: 10,
    fontWeight: '900',
    letterSpacing: 0.4,
    textTransform: 'uppercase',
  },
  useHeroTitle: {
    marginTop: 2,
    color: PADIPRO.text,
    fontWeight: '900',
    fontSize: 16,
  },
  useHeroStockBadge: {
    minWidth: 62,
    borderWidth: 1,
    borderColor: PADIPRO.borderStrong,
    backgroundColor: PADIPRO.surface,
    borderRadius: 14,
    paddingHorizontal: 9,
    paddingVertical: 7,
    alignItems: 'center',
  },
  useHeroStockNum: {
    color: PADIPRO.primary,
    fontWeight: '900',
    fontSize: 18,
  },
  useHeroStockUnit: {
    marginTop: 1,
    color: PADIPRO.muted,
    fontWeight: '800',
    fontSize: 10,
  },
  useMetricGrid: {
    marginTop: 10,
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 7,
  },
  useMetricCard: {
    flex: 1,
    minWidth: 88,
    borderWidth: 1,
    borderColor: PADIPRO.border,
    backgroundColor: PADIPRO.surface,
    borderRadius: 13,
    paddingHorizontal: 8,
    paddingVertical: 8,
  },
  useMetricLabel: {
    color: PADIPRO.muted,
    fontSize: 9,
    fontWeight: '900',
    textTransform: 'uppercase',
  },
  useMetricValue: {
    marginTop: 3,
    color: PADIPRO.textStrong,
    fontSize: 11,
    fontWeight: '900',
  },
  useHeroWarn: {
    marginTop: 8,
    color: PADIPRO.warning,
    fontWeight: '800',
    fontSize: 12,
    lineHeight: 17,
  },

  infoCard: {
    marginTop: 12,
    borderWidth: 1,
    borderColor: `${PADIPRO.sky}40`,
    backgroundColor: PADIPRO.skySoft,
    borderRadius: 14,
    padding: 12,
  },
  infoTitle: { color: PADIPRO.text, fontWeight: '900' },
  infoText: { color: PADIPRO.muted, fontWeight: '700', marginTop: 4 },

  rowWrap: { flexDirection: 'row', flexWrap: 'wrap', marginTop: 8, gap: 8 },

  allocBox: {
    marginTop: 10,
    borderWidth: 1,
    borderColor: PADIPRO.border,
    borderRadius: 18,
    padding: 10,
    backgroundColor: PADIPRO.surfaceSunken,
  },
  allocHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 10,
  },
  allocTitle: { color: PADIPRO.text, fontWeight: '900', fontSize: 14 },
  allocSubTitle: {
    marginTop: 2,
    color: PADIPRO.muted,
    fontWeight: '800',
    fontSize: 11,
  },

  refreshMiniBtn: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
    paddingHorizontal: 10,
    paddingVertical: 8,
    borderRadius: 999,
    borderWidth: 1,
    borderColor: PADIPRO.primarySoftStrong,
    backgroundColor: PADIPRO.primarySoft,
  },
  refreshMiniText: { color: PADIPRO.primary, fontWeight: '900' },
  allocToolsRow: {
    marginTop: 10,
    flexDirection: 'row',
    gap: 8,
  },
  allocToolBtn: {
    flex: 1,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 6,
    borderRadius: 12,
    paddingVertical: 9,
    borderWidth: 1,
    borderColor: PADIPRO.primarySoftStrong,
    backgroundColor: PADIPRO.primarySoft,
  },
  allocToolText: { color: PADIPRO.primary, fontWeight: '900', fontSize: 12 },
  allocToolBtnGhost: {
    paddingHorizontal: 12,
    justifyContent: 'center',
    borderRadius: 12,
    borderWidth: 1,
    borderColor: PADIPRO.border,
    backgroundColor: PADIPRO.surface,
  },
  allocToolGhostText: { color: PADIPRO.muted, fontWeight: '900', fontSize: 12 },
  allocMethodCard: {
    marginTop: 10,
    borderWidth: 1,
    borderColor: PADIPRO.border,
    backgroundColor: PADIPRO.surface,
    borderRadius: 16,
    paddingHorizontal: 10,
    paddingVertical: 10,
  },
  allocMethodTitle: {
    color: PADIPRO.textStrong,
    fontWeight: '900',
    fontSize: 12,
    textTransform: 'uppercase',
    letterSpacing: 0.3,
  },

  emptyFields: {
    marginTop: 10,
    padding: 12,
    borderRadius: 12,
    backgroundColor: PADIPRO.surface,
    borderWidth: 1,
    borderColor: PADIPRO.border,
  },
  emptyFieldsTitle: { color: PADIPRO.textStrong, fontWeight: '900' },
  emptyFieldsText: { color: PADIPRO.muted, fontWeight: '700', marginTop: 4 },

  fieldsListBox: {
    marginTop: 10,
    maxHeight: 238,
    borderRadius: 16,
    borderWidth: 1,
    borderColor: PADIPRO.border,
    backgroundColor: PADIPRO.surface,
    paddingHorizontal: 8,
    paddingBottom: 8,
  },
  fieldsListBoxHandset: {
    maxHeight: 190,
  },

  fieldRow: {
    marginTop: 8,
    padding: 10,
    borderRadius: 14,
    borderWidth: 1,
    borderColor: PADIPRO.border,
    backgroundColor: PADIPRO.surface,
    flexDirection: 'row',
    alignItems: 'center',
    gap: 10,
  },
  fieldRowActive: {
    borderColor: PADIPRO.primary,
    backgroundColor: PADIPRO.primarySoft,
  },
  fieldName: { color: PADIPRO.text, fontWeight: '900' },
  fieldMeta: { color: PADIPRO.muted, fontWeight: '700', marginTop: 2, fontSize: 12 },
  methodHint: {
    marginTop: 6,
    color: PADIPRO.muted,
    fontSize: 11,
    fontWeight: '800',
    lineHeight: 16,
  },

  previewBox: {
    marginTop: 10,
    borderWidth: 1,
    borderColor: PADIPRO.border,
    backgroundColor: PADIPRO.surface,
    borderRadius: 16,
    padding: 11,
  },
  previewTitle: { color: PADIPRO.text, fontWeight: '900' },
  previewText: { color: PADIPRO.muted, fontWeight: '700', marginTop: 4 },
  previewStrong: { color: PADIPRO.text, fontWeight: '900' },
  previewWarn: { color: PADIPRO.warning, fontWeight: '700', marginTop: 8, lineHeight: 18 },

  splitRow: {
    marginTop: 8,
    flexDirection: 'row',
    justifyContent: 'space-between',
    gap: 10,
  },
  splitName: { flex: 1, color: PADIPRO.text, fontWeight: '800' },
  splitAmt: { color: PADIPRO.text, fontWeight: '900' },
  splitMoreText: {
    marginTop: 8,
    color: PADIPRO.muted,
    fontWeight: '800',
    fontSize: 12,
  },

  modalScrollContent: {
    paddingBottom: 16,
  },

  modalFooter: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    marginTop: 8,
    paddingTop: 10,
    borderTopWidth: 1,
    borderTopColor: PADIPRO.border,
    backgroundColor: PADIPRO.surface,
  },
  footerSummary: {
    flex: 1.05,
    minWidth: 96,
    borderWidth: 1,
    borderColor: PADIPRO.border,
    backgroundColor: PADIPRO.surface,
    borderRadius: 14,
    paddingHorizontal: 10,
    paddingVertical: 8,
  },
  footerSummaryLabel: {
    color: PADIPRO.muted,
    fontSize: 10,
    fontWeight: '900',
    textTransform: 'uppercase',
  },
  footerSummaryValue: {
    marginTop: 2,
    color: PADIPRO.textStrong,
    fontSize: 13,
    fontWeight: '900',
  },

  modalActionsRow: { flexDirection: 'row', marginTop: 14, gap: 10 },
  modalBtn: { flex: 1, padding: 12, borderRadius: 14, alignItems: 'center' },
  modalCancel: { backgroundColor: PADIPRO.surfaceSunken },
  modalCancelText: { color: PADIPRO.textStrong, fontWeight: '900' },
  modalSave: { backgroundColor: PADIPRO.primary },
  modalSaveText: { color: '#fff', fontWeight: '900' },

  modalBackdropCenter: {
    flex: 1,
    backgroundColor: 'rgba(15,23,42,.45)',
    alignItems: 'center',
    justifyContent: 'center',
  },
  printCard: {
    width: '88%',
    backgroundColor: PADIPRO.surface,
    borderRadius: 18,
    padding: 16,
    borderWidth: 1,
    borderColor: PADIPRO.border,
    ...padiproShadowMd,
  },
  printTitle: { fontWeight: '900', fontSize: 18, color: PADIPRO.textStrong },
  printSub: { marginTop: 4, color: PADIPRO.muted, fontWeight: '700' },
  sizeRow: { flexDirection: 'row', gap: 8, marginTop: 10 },
  sizeChip: {
    paddingVertical: 10,
    paddingHorizontal: 14,
    borderRadius: 999,
    borderWidth: 1,
    borderColor: PADIPRO.border,
    backgroundColor: PADIPRO.surface,
  },
  sizeChipActive: { backgroundColor: PADIPRO.primarySoft, borderColor: PADIPRO.primary },
  sizeChipText: { fontWeight: '900', color: PADIPRO.muted },
  sizeChipTextActive: { color: PADIPRO.primary },
  hint: { marginTop: 10, color: PADIPRO.muted, fontSize: 12, textAlign: 'center', fontWeight: '600' },
});
